import React from 'react';
import {
  Brief,
  Modal,
  ModalSection,
  Gutter,
  Form,
  Icon,
  Layout,
  LayoutItem,
  Radio,
  ParagraphHeader,
  useApi,
  useClearApiCacheOnUnmount,
  CondensedLarge,
} from '@axiom/ui';
import type { Contact } from '@axiom/validation';

import type { InterviewCandidate } from '../../types/interview-candidate.types';
import { TalentInterviewModalHeader } from '../TalentInterviewModalHeader/TalentInterviewModalHeader';
import { TalentInterviewModalFooter } from '../TalentInterviewModalFooter/TalentInterviewModalFooter';
import { OpportunityIntakeApi } from '../../api/opportunity-intake-api';
import { TalentInterviewEngagementSelectionSchema } from '../../schema/talent-interview-engagement-selection-schema';

export interface TalentInterviewEngagementSelectionModalProps {
  candidate: InterviewCandidate;
  contactId: Contact['id'];
  onClose: () => void;
  onContinue: (data: {
    engagementChoice: 'existing' | 'new';
    submissionId?: string;
  }) => void;
}

export const TalentInterviewEngagementSelectionModal = ({
  candidate,
  contactId,
  onClose,
  onContinue,
}: TalentInterviewEngagementSelectionModalProps) => {
  const [{ data: opportunities }] = useApi(
    OpportunityIntakeApi.readContactOpportunitiesForCandidate(
      candidate.id,
      contactId
    )
  );

  useClearApiCacheOnUnmount(
    OpportunityIntakeApi,
    `/intake/opportunity/candidates/${candidate.id}/contacts/${contactId}/options`
  );

  const handleSubmit = (engagementSelection: string) => {
    const isNewEngagement = engagementSelection === 'new';
    onContinue({
      engagementChoice: isNewEngagement ? 'new' : 'existing',
      submissionId: isNewEngagement ? undefined : engagementSelection,
    });
  };

  return (
    <Form
      name="ENGAGEMENT_SELECTION_FORM"
      schema={TalentInterviewEngagementSelectionSchema}
      initialValues={{
        engagementSelection: 'new',
      }}
      onSubmit={formData => {
        handleSubmit(formData.engagementSelection ?? 'new');
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="ENGAGEMENT_SELECTION_MODAL">
            <TalentInterviewModalHeader
              candidate={candidate}
              name="ENGAGEMENT_SELECTION_MODAL_HEADER"
              onClose={onClose}
            />
            <ModalSection>
              <ParagraphHeader name="SECTION_TITLE">
                Which engagement are you interviewing{' '}
                {candidate.calculatedFirstName ||
                  candidate.calculatedDisplayName}{' '}
                for?
              </ParagraphHeader>
              <Gutter bottom="24px" />
              <Brief
                name="ENGAGEMENT_OPTIONS"
                briefMaxChildren={3}
                pattern="secondary"
                hideMask
              >
                <Gutter bottom="16px">
                  <Radio
                    name="engagementSelection"
                    displayValue={
                      <Gutter vertical="8px" horizontal="8px">
                        <Layout horizontalGutter="8px" position="middle">
                          <Icon name="plus" />
                          <LayoutItem>
                            <CondensedLarge>
                              Create a new engagement
                            </CondensedLarge>
                          </LayoutItem>
                        </Layout>
                      </Gutter>
                    }
                    option="new"
                    mode="tile"
                  />
                </Gutter>
                {opportunities.map(opportunity => (
                  <Gutter key={opportunity.submissionId} bottom="16px">
                    <Radio
                      name="engagementSelection"
                      displayValue={
                        <Gutter vertical="8px" horizontal="8px">
                          <CondensedLarge>{opportunity.jobName}</CondensedLarge>
                        </Gutter>
                      }
                      option={opportunity.submissionId}
                      mode="tile"
                    />
                  </Gutter>
                ))}
              </Brief>
            </ModalSection>
            <TalentInterviewModalFooter
              name="ENGAGEMENT_SELECTION_MODAL_FOOTER"
              onClose={onClose}
              onContinue={async () => {
                await fireSubmit();
              }}
            />
          </Modal>
        );
      }}
    </Form>
  );
};
